import type { DrainageBoundary, SoilLayer } from "../domain/types";

export const WATER_UNIT_WEIGHT = 9.81;

export interface StressSegment {
  depthTop: number;
  depthBottom: number;
  gamma: number;
  gammaSat: number;
}

export interface ConsolidationSettlementInput {
  initialEffectiveStress: number;
  preconsolidationStress: number;
  voidRatio: number;
  compressionIndex: number;
  recompressionIndex: number;
  thickness: number;
  stressIncrement: number;
}

export function calculateConsolidationSettlement(input: ConsolidationSettlementInput): number {
  const {
    initialEffectiveStress: s0,
    preconsolidationStress: sp,
    voidRatio: e0,
    compressionIndex: Cc,
    recompressionIndex: Cr,
    thickness: h,
    stressIncrement: ds,
  } = input;
  if (![s0, sp, e0, Cc, Cr, h, ds].every(Number.isFinite) || ds <= 0 || s0 <= 0 || h <= 0) return 0;
  if (s0 >= sp) return (Cc / (1 + e0)) * h * Math.log10((s0 + ds) / s0);
  if (s0 + ds <= sp) return (Cr / (1 + e0)) * h * Math.log10((s0 + ds) / s0);
  if (sp <= 0) return 0;
  return (Cr / (1 + e0)) * h * Math.log10(sp / s0)
    + (Cc / (1 + e0)) * h * Math.log10((s0 + ds) / sp);
}

export function effectiveStressIncrement(
  segment: StressSegment,
  fromDepth: number,
  toDepth: number,
  groundwaterDepth: number,
): number {
  if (toDepth <= fromDepth) return 0;
  const top = Math.max(fromDepth, segment.depthTop);
  const bottom = Math.min(toDepth, segment.depthBottom);
  if (bottom <= top) return 0;
  const dryThickness = Math.max(0, Math.min(bottom, groundwaterDepth) - top);
  const wetThickness = Math.max(0, bottom - Math.max(top, groundwaterDepth));
  return segment.gamma * dryThickness + (segment.gammaSat - WATER_UNIT_WEIGHT) * wetThickness;
}

export function getDrainagePath(thickness: number, boundary: DrainageBoundary): number {
  if (!Number.isFinite(thickness) || thickness <= 0) return 0;
  return boundary === "double" ? thickness / 2 : thickness;
}

export function getThicknessWeightedCv(layers: readonly Partial<SoilLayer>[], fallback = 1.5): number {
  const cohesive = layers.filter((layer) =>
    (layer.type === "cohesive" || Number(layer.Cc) > 0.05)
    && Number.isFinite(layer.Cv)
    && Number.isFinite(layer.thickness)
    && Number(layer.thickness) > 0,
  );
  const totalThickness = cohesive.reduce((sum, layer) => sum + Number(layer.thickness), 0);
  if (totalThickness <= 0) return fallback;
  return cohesive.reduce((sum, layer) => sum + Number(layer.Cv) * Number(layer.thickness), 0) / totalThickness;
}

export function osterbergHalfInfluence(a: number, b: number, z: number): number {
  if (z <= 1e-6) return 0.5;
  const alpha1 = Math.atan((a * b) / (z * Math.sqrt(a * a + b * b + z * z)));
  const alpha2 = Math.atan(b / Math.sqrt(a * a + z * z));
  return (((a + b) / a) * (alpha1 + alpha2) - (b / a) * alpha2) / (2 * Math.PI);
}

export function stripLoadInfluenceCenterline(width: number, depth: number): number {
  if (depth <= 1e-6) return 1;
  const halfWidth = width / 2;
  const angle = Math.atan(halfWidth / depth);
  return Math.max(0, Math.min(1, (2 / Math.PI) * (angle + Math.sin(angle) * Math.cos(angle))));
}
